import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Location, MatchResponse, PostCode, SurveyData } from '../../services/listingsSearchCriteriaApi';
import { buildPostcodeIndex } from '../../postcodeUtils';
import { showBackButton } from '../telegram';
import { BudgetStep, ExtrasStep, IntroStep, ModeStep, ReviewStep } from './Steps';
import WhereStep from './WhereStep';
import {
  initialWizardState,
  invalidHint,
  isStepValid,
  progressStep,
  wizardReducer,
  wizardToSurveyData,
  WizardStep,
} from './wizardState';

const MATCH_DEBOUNCE_MS = 400;
const MATCHING_STEPS: WizardStep[] = ['where', 'budget', 'extras', 'review'];

type ReferenceApi = {
  fetchLocations(opts?: { includeGeoArea?: boolean }): Promise<Location[]>;
  fetchPostcodes(): Promise<PostCode[]>;
  matchListings(criteria: SurveyData): Promise<MatchResponse>;
};

type Props = {
  referenceApi: ReferenceApi;
  onSubmit: (data: SurveyData) => Promise<void>;
  onCancel?: () => void;
};

function mainLabel(step: WizardStep, fromReview: boolean): string {
  if (fromReview) return 'Save changes';
  switch (step) {
    case 'intro': return 'Get started';
    case 'extras': return 'Review my search';
    case 'review': return 'Save and start alerts';
    default: return 'Continue';
  }
}

export default function SearchWizard({ referenceApi, onSubmit, onCancel }: Props) {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const [locations, setLocations] = useState<Location[]>([]);
  const [postcodes, setPostcodes] = useState<PostCode[]>([]);
  const [matched, setMatched] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    referenceApi.fetchLocations({ includeGeoArea: true })
      .then(setLocations)
      .catch(err => console.error('Failed to load locations', err));
    referenceApi.fetchPostcodes()
      .then(setPostcodes)
      .catch(err => console.error('Failed to load postcodes', err));
  }, [referenceApi]);

  const postcodeIndex = useMemo(() => buildPostcodeIndex(postcodes), [postcodes]);
  const locationNames = useMemo(() => new Map(locations.map(location => [location.id, location.name])), [locations]);

  const surveyData = useMemo(() => wizardToSurveyData(state), [state]);
  const surveyDataKey = JSON.stringify(surveyData);
  const hasLocation = state.mode === 'postcode' ? state.postcodes.length > 0 : state.areas.length > 0;
  const showMatching = MATCHING_STEPS.includes(state.step) && hasLocation;

  useEffect(() => {
    if (!showMatching) {
      setMatched(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      referenceApi.matchListings(JSON.parse(surveyDataKey))
        .then(response => { if (!cancelled) setMatched(response.count); })
        .catch(err => console.error('Failed to match listings', err));
    }, MATCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [referenceApi, showMatching, surveyDataKey]);

  const canGoBack = state.step !== 'intro' || !!onCancel;
  useEffect(() => {
    if (!canGoBack) return;
    return showBackButton(() => (state.step === 'intro' ? onCancel?.() : dispatch({ type: 'back' })));
  }, [canGoBack, state.step, onCancel]);

  const valid = isStepValid(state);
  const progress = progressStep(state.step);

  const onMain = async () => {
    if (state.step !== 'review') {
      dispatch({ type: 'next' });
      window.scrollTo?.(0, 0);
      return;
    }
    setSubmitting(true);
    setSubmitError(false);
    try {
      await onSubmit(surveyData);
    } catch (err) {
      console.error('Failed to save search', err);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <header className="ma-header">
        {canGoBack ? (
          <button
            type="button"
            className="ma-link-button"
            onClick={() => (state.step === 'intro' ? onCancel?.() : dispatch({ type: 'back' }))}
          >
            Back
          </button>
        ) : <span className="ma-brand">HomeScout</span>}
        {progress && <span className="ma-step-label" style={{ margin: 0 }}>Step {progress} of 4</span>}
        {state.step === 'review' && <span className="ma-step-label" style={{ margin: 0 }}>Almost done</span>}
      </header>
      <main className="ma-body">
        {state.step === 'intro' && <IntroStep />}
        {state.step === 'mode' && <ModeStep state={state} dispatch={dispatch} />}
        {state.step === 'where' && (
          <WhereStep
            state={state}
            dispatch={dispatch}
            locations={locations}
            postcodes={postcodes}
            postcodeIndex={postcodeIndex}
          />
        )}
        {state.step === 'budget' && <BudgetStep state={state} dispatch={dispatch} />}
        {state.step === 'extras' && <ExtrasStep state={state} dispatch={dispatch} />}
        {state.step === 'review' && <ReviewStep state={state} dispatch={dispatch} locationNames={locationNames} />}
      </main>
      <footer className="ma-footer">
        {progress && (
          <div className="ma-progress" style={{ marginBottom: 12 }}>
            <div style={{ width: `${progress * 25}%` }} />
          </div>
        )}
        {showMatching && matched !== null && matched > 0 && (
          <p className="ma-match">≈ {matched} homes matched in the last 2 weeks</p>
        )}
        {showMatching && matched === 0 && (
          <p className="ma-match ma-match--zero">
            No matches in the last 2 weeks. Try adding more areas or widening your range. You can still continue.
          </p>
        )}
        {!valid && <p className="ma-invalid">{invalidHint(state)}</p>}
        {submitError && <p className="ma-error">Couldn't save your search. Please try again.</p>}
        <button type="button" className="ma-primary" disabled={!valid || submitting} onClick={onMain}>
          {mainLabel(state.step, state.fromReview)}
        </button>
      </footer>
    </>
  );
}
